
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { uploadFilesFast } from '../../util/fileUpload';
import { FormattedMessage } from '../../util/reactIntl';
import { IconCamera, IconClose, IconSpinner } from '../../components/index';
import css from '../../components/TripPanel/SectionsCarPhotos.css';
import imageCompression from 'browser-image-compression';

const ACCEPT_TYPE = 'image/jpeg, image/png, image/gif, image/webp, image/bmp, image/svg+xml, image/tiff, image/heif, image/heic';

const UploadSection = forwardRef(({
  intl,
  isCustomer,
  isPickUp,
  currentUser,
  onNoteChange,
  description,
  setIsUploaded,
  setUploading,
  setLoading,
  type,
  note,
  photosData,
  setPhotosData,
  setActivePhotoSection,
  id,
  setIsExpectedLength,
  setLastImageUrl,
  exteriorPhotoFun
}, ref) => {
  const [uploadFileError, setUploadFileError] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [filesName , setFilesName] = useState()
  const inputElement = useRef(null);

  // Compress image
  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    try {
      if (file.size / 1024 / 1024 < options.maxSizeMB) {
        // Skip compression if file is already smaller than maxSizeMB
        return file;
      }
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error('Image compression error:', error);
      return file;
    }
  };

  useImperativeHandle(ref, () => ({
    handleClickUpload() {
      inputElement.current.click();
    }
  }));

  useEffect(() => {
    if (photosData && photosData.length) {
      setUploadedPhotos(photosData);
      setIsUploaded(true);
    }
  }, [])

  useEffect(() => {
    if(uploadedPhotos.length) {
      handleAddPhoto(uploadedPhotos);
      if (setLastImageUrl && uploadedPhotos.length > 0) {
        setLastImageUrl(filesName);
      }
    }
  },[uploadedPhotos])

  const handleFileChange = async (e) => {
    const input = e.target;
    let selectedFiles = Array.from(input.files).filter(file => file.type.startsWith('image/'));
    
    // Limit fuel gauge and odometer photos to one image each
    if (type === 'fuelGaugePhotos' || type === 'odometerPhotos') {
      selectedFiles = selectedFiles.slice(0, 1);
    }

    if (selectedFiles.length) {
      setLoading(true); // Set loading state before compression starts
      try {
        const compressedFiles = await Promise.all(selectedFiles.map(compressImage));
        await uploadFilesHandler(compressedFiles.map((file, index) => ({ index, file })));
      } catch (error) {
        console.error('Error during file processing:', error);
        setUploadFileError(true);
      } finally {
        setLoading(false); // Ensure loading is turned off after processing
      }
    } else {
      setUploadFileError(true);
    }

    input.value = ''; // Reset file input
  };
  const uploadFilesHandler = async (files) => {

    if (files.length) {
      setIsUploaded(true)
      setUploadFileError(false);
      try {
        const uploadPromises = files.map(file => {
          const formData = new FormData();
          formData.append('photos', file.file, file.file.name);
          if(setLastImageUrl) {
            setFilesName(file.file.name)
          }
          formData.append('userId', currentUser.id.uuid);
          formData.append('tripId', id);
          return uploadFilesFast(formData).then(response => {
            if (response.status !== 200) {
              throw new Error('Upload failed');
            }
            return response.json();
          });
        });
        const responses = await Promise.all(uploadPromises);
        const newPhotos = responses.map((item, index) => ({
          fileUrl: item.url,
          id: id,
          note: item.note,
          type: type,
          isPickUp: true,
          timestamp: new Date().getTime() * (index+1),
          isJustUploaded: true,
        }));
        setUploadedPhotos(prevPhotos => [...prevPhotos, ...newPhotos]);
        setActivePhotoSection(newPhotos)
        setLoading(false);
        if(type == "exteriorPhotos") exteriorPhotoFun(newPhotos)
      } catch (error) {
        console.error('Upload error:', error);
        setUploadFileError(true);
        setLoading(false);
        setIsUploaded(false)
      }
    }
  };


  const handleAddPhoto = () => {
        const allPhotos = []
        uploadedPhotos.map(i => {
        const data = {
        fileUrl: i.fileUrl,
        note: note,
        timestamp: i.timestamp,
        isCustomer: isCustomer,
        isPickUp: isPickUp,
        [type]: true,
      };
      allPhotos.push(data);
    });
    console.log("isPickUpUpload section", isPickUp)
    setPhotosData(allPhotos)
  }

  const handleRemovePhoto = (timestamp) => {
    // Remove photo from uploadedPhotos
    const filteredUploadedPhotos = uploadedPhotos.filter(photo => photo.timestamp !== timestamp);
    setUploadedPhotos(filteredUploadedPhotos);

    // Remove photo from photosData
    const filteredPhotosData = photosData.filter(photo => photo.timestamp !== timestamp);
    setPhotosData(filteredPhotosData);
  };

  useEffect(() => {
    uploadedPhotos.length ? setIsUploaded(true) : setIsUploaded(false);
    if (setIsExpectedLength) {
      setIsExpectedLength(uploadedPhotos.length);
    }

    if (uploadedPhotos.length === 0) {
      setUploadFileError(false);
    }
  }, [uploadedPhotos.length]);

  const uploadTextLabel = <FormattedMessage id="SectionUploadCarStatus.remarks" />;
  const uploadPlaceholder = intl.formatMessage({ id: 'SectionUploadCarStatus.uploadPlaceholder' });

  return (
    <div>
      <div className={ `${css.pickUpPhotosHolder} ${css.startTripPickUpPhotosHolder}`}>
        <div>
          <div className={css.uploadBox} onClick={() => ref.current.handleClickUpload()}>
          </div>
          <input
            accept={ACCEPT_TYPE}
            className={css.inputField}
            type="file"
            name="uploadPhotoStatus"
            id="uploadPhotoStatus"
            multiple={type !== 'fuelGaugePhotos' && type !== 'odometerPhotos'}
            onChange={handleFileChange}
            ref={inputElement}
          />
        </div>
        {isCustomer && isPickUp && <div className={css.pickUpReading}>{description}</div>}
        {uploadedPhotos.length > 0 && (
          <div className={classNames(css.photosContainer, css.pickUpPhotosContainer)}>
            {uploadedPhotos.map((photo, index) => (
              <div className={css.photoWrapper} key={index}>
                <div className={css.photoWrapperInner}>
                  <div
                    className={css.removePhotoBtn}
                    onClick={() => handleRemovePhoto(photo.timestamp)}
                  >
                    <IconClose size={'small'} />
                  </div>
                  <img
                    src={photo.fileUrl}
                    className={css.tripPhoto}
                    onClick={() => window.open(photo.fileUrl, '_blank')}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {uploadFileError && (
        <p className={css.uploadError}>
          <FormattedMessage id="SectionUploadCarStatus.uploadError" />
        </p>
      )}
    </div>
  );
});

UploadSection.propTypes = {
  intl: PropTypes.object.isRequired,
  isCustomer: PropTypes.bool.isRequired,
  isPickUp: PropTypes.bool.isRequired,
  currentUser: PropTypes.object.isRequired,
  onNoteChange: PropTypes.func,
  description: PropTypes.string,
  setIsUploaded: PropTypes.any
};

export default UploadSection;
